// Copyright © 2019-2026 Focal Systems. All rights reserved.

import i18n from "i18next";
import {
  type AssistantEvent,
  type AssistantRequest,
  type AssistantTransport,
  MAX_ANSWER_CHARS,
  MAX_HISTORY_TURNS,
  MAX_QUESTION_CHARS,
} from "@/features/assistant/types";

type Translate = (key: string) => string;

const isString = (v: unknown): v is string => typeof v === "string";
const isStringArray = (v: unknown): v is string[] => Array.isArray(v) && v.every(isString);

/** Runtime guard: the stream is external input, so the reducer only ever sees events of a known shape. */
export function isAssistantEvent(value: unknown): value is AssistantEvent {
  if (typeof value !== "object" || value === null) return false;
  const e = value as Record<string, unknown>;
  switch (e.type) {
    case "step":
      return isString(e.id) && isString(e.label) && (e.status === "running" || e.status === "done");
    case "text":
      return isString(e.delta);
    case "table": {
      const t = e.table as Record<string, unknown> | undefined;
      return !!t && isStringArray(t.columns) && Array.isArray(t.rows) && t.rows.every(isStringArray);
    }
    case "link": {
      const l = e.link as Record<string, unknown> | undefined;
      return !!l && isString(l.label) && isString(l.href);
    }
    case "note":
      return isString(e.text);
    case "error":
      return isString(e.message);
    case "done":
      return true;
    default:
      return false;
  }
}

/**
 * Splits an SSE buffer into complete `data:` frames and the unfinished remainder. Accepts CRLF line
 * endings and `data:` with or without the optional space, as the SSE spec allows.
 */
export function parseSse(buffer: string): { events: unknown[]; rest: string } {
  const frames = buffer.replace(/\r\n/g, "\n").split("\n\n");
  const rest = frames.pop() ?? "";
  const events: unknown[] = [];
  for (const frame of frames) {
    const data = frame
      .split("\n")
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice(line.startsWith("data: ") ? 6 : 5))
      .join("\n");
    if (!data) continue;
    try {
      events.push(JSON.parse(data));
    } catch {
      // A frame that is not JSON is dropped, never shown.
    }
  }
  return { events, rest };
}

/** Cut by code point, as the server counts, so a surrogate pair is never split. */
function clamp(text: string, max: number): string {
  return text.length <= max ? text : Array.from(text).slice(0, max).join("");
}

/**
 * The thread grows without bound, but the server takes only the latest turns, each within its limits:
 * anything more would refuse every follow-up question with a 422.
 */
function withinLimits(request: AssistantRequest): AssistantRequest {
  return {
    ...request,
    history: request.history.slice(-MAX_HISTORY_TURNS).map((turn) => ({
      question: clamp(turn.question, MAX_QUESTION_CHARS),
      answer: clamp(turn.answer, MAX_ANSWER_CHARS),
    })),
  };
}

/**
 * The server's fixed sentences (refusals, limit and timeout notes, errors) carry a `code`; the panel shows
 * its translation, and the server's English sentence only for a code it has no translation for.
 */
function serverText(code: unknown, sentence: string, t: Translate): string {
  if (!isString(code) || !code) return sentence;
  const key = `assistant.server.${code}`;
  const translated = t(key);
  return translated === key ? sentence : translated;
}

function localized(event: AssistantEvent, t: Translate): AssistantEvent {
  const code = (event as { code?: unknown }).code;
  if (event.type === "note") return { type: "note", text: serverText(code, event.text, t) };
  if (event.type === "error") return { type: "error", message: serverText(code, event.message, t) };
  return event;
}

/**
 * The server refuses with `{"error": "<sentence>", "code": "<code>"}` (focal-systems/bot-backoffice, src/assistant/app.py). Any
 * other body came from somewhere else (ingress, proxy, framework validation), so its status says nothing
 * reliable.
 */
async function refusalMessage(response: Response, t: Translate): Promise<string> {
  let body: Record<string, unknown> | null;
  try {
    body = (await response.json()) as Record<string, unknown> | null;
  } catch {
    return t("assistant.error.generic");
  }
  const error = body?.error;
  if (!isString(error) || !error) return t("assistant.error.generic");
  // The app's own 401 handling (src/api/client.ts) is inline in the API middleware, with no hook to
  // call from here, so an expired session is reported as an error the viewer can act on.
  return response.status === 401 ? t("assistant.error.sessionExpired") : serverText(body?.code, error, t);
}

export function createSseTransport(
  baseUrl: string,
  getToken: () => string | null,
  // The app's i18next instance (initialised in src/lib/i18n), read when a message is shown.
  t: Translate = (key) => i18n.t(key),
): AssistantTransport {
  return {
    async *ask(request: AssistantRequest, signal: AbortSignal): AsyncIterable<AssistantEvent> {
      let response: Response;
      try {
        response = await fetch(`${baseUrl}/v1/ask`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken() ?? ""}` },
          body: JSON.stringify(withinLimits(request)),
          signal,
        });
      } catch {
        if (signal.aborted) return;
        yield { type: "error", message: t("assistant.error.unreachable") };
        return;
      }
      if (!response.ok || !response.body) {
        const message = await refusalMessage(response, t);
        if (!signal.aborted) yield { type: "error", message };
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finished = false;
      try {
        while (!finished) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parsed = parseSse(buffer);
          buffer = parsed.rest;
          for (const event of parsed.events) {
            if (signal.aborted) return;
            if (!isAssistantEvent(event)) continue;
            yield localized(event, t);
            if (event.type === "done" || event.type === "error") finished = true;
          }
        }
        // A stream that closes without `done` or `error` lost its end (proxy timeout, pod restart).
        if (!finished && !signal.aborted) yield { type: "error", message: t("assistant.error.cutOff") };
      } catch {
        if (!signal.aborted) yield { type: "error", message: t("assistant.error.connectionLost") };
      } finally {
        reader.releaseLock();
      }
    },
  };
}
